'use client';

import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel } from 'swiper/modules';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import 'swiper/css';
import { useParams } from 'next/navigation';
import { loadHighlights } from '@/lib/db';

const IMAGE_DURATION = 8000;
const PROGRESS_INTERVAL = 100;

export default function StoryWidget() {
  const { lang } = useParams();
  const currentLang = (lang as string) || 'fa';
  const [highlights, setHighlights] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeHighlight, setActiveHighlight] = useState<any>(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<any>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const [videoProgress, setVideoProgress] = useState<Record<number, number>>({});
  const [videoDuration, setVideoDuration] = useState<Record<number, number>>({});
  const [currentTime, setCurrentTime] = useState<Record<number, number>>({});
  const [imageProgress, setImageProgress] = useState<Record<number, number>>({});
  const [imageTimer, setImageTimer] = useState<NodeJS.Timeout | null>(null);
  const [progressTimer, setProgressTimer] = useState<NodeJS.Timeout | null>(null);

  const activeHighlightRef = useRef(activeHighlight);
  const currentStoryIndexRef = useRef(currentStoryIndex);

  useEffect(() => {
    activeHighlightRef.current = activeHighlight;
  }, [activeHighlight]);

  useEffect(() => {
    currentStoryIndexRef.current = currentStoryIndex;
  }, [currentStoryIndex]);

  useEffect(() => {
    loadHighlights()
      .then(data => {
        if (data && data.length) {
          const sorted = [...data].sort((a, b) => b.id - a.id);
          setHighlights(sorted);
        } else {
          setHighlights([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    return () => {
      if (imageTimer) clearTimeout(imageTimer);
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [imageTimer, progressTimer]);

  const getLocalized = (item: any, field: string) =>
    currentLang === 'en' ? item[`${field}_en`] || item[field] :
    currentLang === 'ar' ? item[`${field}_ar`] || item[field] : item[field];

  const isVideoSource = (src: string) =>
    src && (src.startsWith('data:video/') || /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i.test(src));

  const scroll = (dir: number) => scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });

  const clearImageTimers = () => {
    if (imageTimer) { clearTimeout(imageTimer); setImageTimer(null); }
    if (progressTimer) { clearInterval(progressTimer); setProgressTimer(null); }
  };

  const startImageTimer = (story: any) => {
    if (!story) return;
    clearImageTimers();
    const storyId = story.id;
    setImageProgress(prev => ({ ...prev, [storyId]: 0 }));

    const interval = setInterval(() => {
      setImageProgress(prev => {
        const current = prev[storyId] || 0;
        const next = Math.min(current + (PROGRESS_INTERVAL / IMAGE_DURATION) * 100, 100);
        return { ...prev, [storyId]: next };
      });
    }, PROGRESS_INTERVAL);
    setProgressTimer(interval);

    const timeout = setTimeout(() => {
      clearImageTimers();
      setImageProgress(prev => ({ ...prev, [storyId]: 100 }));
      const currentStory = activeHighlightRef.current?.stories?.[currentStoryIndexRef.current];
      if (currentStory && !isVideoSource(currentStory.image) && currentStory.id === storyId) {
        goToNextStory();
      }
    }, IMAGE_DURATION);
    setImageTimer(timeout);
  };

  // ========== ناوبری استوری‌ها ==========
  const goToPrevStory = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    clearImageTimers();
    if (currentStoryIndex > 0) {
      const newIndex = currentStoryIndex - 1;
      setCurrentStoryIndex(newIndex);
      if (swiperRef.current) {
        swiperRef.current.slideTo(newIndex);
      }
    }
  };

  const goToNextStory = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    clearImageTimers();
    if (!activeHighlight) return;
    const stories = activeHighlight.stories || [];
    if (currentStoryIndex < stories.length - 1) {
      const newIndex = currentStoryIndex + 1;
      setCurrentStoryIndex(newIndex);
      if (swiperRef.current) {
        swiperRef.current.slideTo(newIndex);
      }
    } else {
      // آخرین استوری → هایلایت بعدی
      const fakeEvent = { stopPropagation: () => {}, preventDefault: () => {} } as React.MouseEvent;
      goToNextHighlight(fakeEvent);
    }
  };

  // ========== ناوبری هایلایت‌ها ==========
  const goToNextHighlight = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    clearImageTimers();
    if (!activeHighlight) return;
    const currentIndex = highlights.findIndex(h => h.id === activeHighlight.id);
    const nextHighlight = highlights[currentIndex + 1];
    if (nextHighlight) {
      setActiveHighlight(nextHighlight);
      setCurrentStoryIndex(0);
      setVideoProgress({});
      setVideoDuration({});
      setCurrentTime({});
      setImageProgress({});
    }
  };

  const goToPrevHighlight = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    clearImageTimers();
    if (!activeHighlight) return;
    const currentIndex = highlights.findIndex(h => h.id === activeHighlight.id);
    const prevHighlight = highlights[currentIndex - 1];
    if (prevHighlight) {
      setActiveHighlight(prevHighlight);
      setCurrentStoryIndex(0);
      setVideoProgress({});
      setVideoDuration({});
      setCurrentTime({});
      setImageProgress({});
    }
  };

  const handleStoryClick = (highlight: any) => {
    clearImageTimers();
    setActiveHighlight(highlight);
    setCurrentStoryIndex(0);
    setVideoProgress({});
    setVideoDuration({});
    setCurrentTime({});
    setImageProgress({});
  };

  const handleClose = () => {
    clearImageTimers();
    Object.values(videoRefs.current).forEach(v => v?.pause());
    setActiveHighlight(null);
    setCurrentStoryIndex(0);
  };

  const handleSlideChange = (swiper: any) => {
    const newIndex = swiper.activeIndex;
    setCurrentStoryIndex(newIndex);
  };

  const handleVideoTimeUpdate = (storyId: number, e: any) => {
    const video = e.target;
    if (video.duration) {
      const progress = (video.currentTime / video.duration) * 100;
      setVideoProgress(prev => ({ ...prev, [storyId]: progress }));
      setCurrentTime(prev => ({ ...prev, [storyId]: video.currentTime }));
      if (!videoDuration[storyId]) setVideoDuration(prev => ({ ...prev, [storyId]: video.duration }));
    }
  };

  const handleVideoEnded = () => {
    goToNextStory();
  };

  const toggleVideoPlay = (storyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRefs.current[storyId];
    if (video) {
      if (video.paused) {
        video.play();
        clearImageTimers();
      } else {
        video.pause();
      }
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!activeHighlight) return;
    const stories = activeHighlight.stories || [];
    if (currentStoryIndex >= stories.length) return;
    const story = stories[currentStoryIndex];
    if (!story) return;

    clearImageTimers();
    Object.values(videoRefs.current).forEach(v => v?.pause());

    if (isVideoSource(story.image)) {
      setVideoProgress(prev => ({ ...prev, [story.id]: 0 }));
      const video = videoRefs.current[story.id];
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    } else {
      startImageTimer(story);
    }
  }, [activeHighlight, currentStoryIndex]);

  useEffect(() => {
    if (activeHighlight && swiperRef.current) {
      swiperRef.current.slideTo(0, 0);
    }
  }, [activeHighlight]);

  if (loading || !highlights.length) return null;

  const totalStories = activeHighlight?.stories?.length || 0;
  const currentHighlightIndex = highlights.findIndex(h => h.id === activeHighlight?.id);

  return (
    <div className="relative py-10 px-4 w-full bg-gray-100" dir="rtl">
      <div className="relative group">
        <div ref={scrollRef} className="flex flex-row gap-4 overflow-x-auto scrollbar-hide w-full pb-4 scroll-smooth">
          {highlights.map((highlight: any) => (
            <div key={highlight.id} className="flex flex-col items-center gap-2 min-w-[100px] cursor-pointer flex-shrink-0 transition-transform hover:scale-105" onClick={() => handleStoryClick(highlight)}>
              <div className="relative w-[85px] h-[85px]">
                <div className="absolute inset-0 rounded-full bg-black p-[3px] shadow-lg">
                  <div className="w-full h-full rounded-full border-2 border-white overflow-hidden bg-white">
                    <img src={highlight.image_cover?.url || '/placeholder.jpg'} alt={getLocalized(highlight, 'title')} className="w-full h-full rounded-full object-cover p-0.5" onError={e => e.currentTarget.src = '/placeholder.jpg'} />
                  </div>
                </div>
              </div>
              <div className="text-center w-[90px]"><span className="text-gray-800 text-xs font-medium truncate block">{getLocalized(highlight, 'title')}</span></div>
            </div>
          ))}
        </div>
        <button onClick={() => scroll(-1)} className="absolute left-0 top-12 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-xl z-10 transition-all opacity-0 group-hover:opacity-100">◀</button>
        <button onClick={() => scroll(1)} className="absolute right-0 top-12 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-xl z-10 transition-all opacity-0 group-hover:opacity-100">▶</button>
      </div>

      {activeHighlight && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md animate-fadeIn">
          <button
            onClick={goToPrevHighlight}
            className={`absolute right-5 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full p-2 transition-all duration-300 hover:scale-110 border border-white/30 ${
              currentHighlightIndex <= 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
            }`}
            disabled={currentHighlightIndex <= 0}
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={goToNextHighlight}
            className={`absolute left-5 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full p-2 transition-all duration-300 hover:scale-110 border border-white/30 ${
              currentHighlightIndex >= highlights.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
            }`}
            disabled={currentHighlightIndex >= highlights.length - 1}
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <div className="relative w-72 max-w-md h-[85vh]">
            <button onClick={handleClose} className="absolute top-5 right-4 z-50 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full p-2 transition-all duration-300 hover:scale-110 border border-white/30">
              <X className="w-4h-4 text-white" />
            </button>

            <div className="absolute top-5 left-4 z-40 flex items-center gap-2 text-white">
              <div className="w-8 h-8 rounded-full bg-black p-0.5 shadow-lg">
                <img src={activeHighlight.image_cover?.url || '/placeholder.jpg'} className="w-full h-full rounded-full object-cover" alt={getLocalized(activeHighlight, 'title')} />
              </div>
              <div>
                <p className="font-bold text-xs drop-shadow-lg">{getLocalized(activeHighlight, 'title')}</p>
                <p className="text-xs text-white/70 drop-shadow-lg">{currentStoryIndex + 1} از {totalStories}</p>
              </div>
            </div>

            {/* ===== نوار پیشرفت از راست به چپ ===== */}
            <div className="absolute top-0 left-0 right-0 z-40 flex gap-1 p-2" dir="rtl">
              {activeHighlight.stories?.map((_: any, index: number) => {
                const storyId = activeHighlight.stories[index]?.id;
                const isVideo = isVideoSource(activeHighlight.stories[index]?.image);
                let progress = 0;
                if (isVideo) {
                  progress = videoProgress[storyId] || 0;
                } else {
                  progress = imageProgress[storyId] || 0;
                }
                return (
                  <div key={index} className="h-1 w-10 flex-1 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white transition-all duration-100"
                      style={{
                        width: index < currentStoryIndex ? '100%' :
                               index === currentStoryIndex ? `${progress}%` : '0%'
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div className="absolute inset-0 z-10 flex">
              <div
                className="w-1/2 h-full cursor-pointer"
                onClick={goToPrevStory}
              />
              <div
                className="w-1/2 h-full cursor-pointer"
                onClick={goToNextStory}
              />
            </div>

            <Swiper
              modules={[Keyboard, Mousewheel]}
              spaceBetween={0}
              slidesPerView={1}
              keyboard={{ enabled: true }}
              mousewheel={{ enabled: true }}
              className="h-full w-full relative z-0"
              onSlideChange={handleSlideChange}
              onSwiper={swiper => swiperRef.current = swiper}
              initialSlide={0}
            >
              {activeHighlight.stories?.map((story: any) => {
                const isVideo = isVideoSource(story.image);
                const storyId = story.id;
                return (
                  <SwiperSlide key={storyId} className="relative flex flex-col items-center rounded justify-center bg-transparent">
                    {story.image && story.image !== '123' ? (
                      isVideo ? (
                        <video
                          ref={el => { if (el) videoRefs.current[storyId] = el; }}
                          src={story.image}
                          className="relative w-full h-full object-contain rounded"
                          playsInline
                          muted={false}
                          onTimeUpdate={e => handleVideoTimeUpdate(storyId, e)}
                          onEnded={handleVideoEnded}
                          onClick={e => toggleVideoPlay(storyId, e)}
                        />
                      ) : (
                        <img
                          src={story.image}
                          alt={getLocalized(story, 'title')}
                          className="relative h-full w-full object-contain rounded"
                        />
                      )
                    ) : story.image_cover?.url ? (
                      <img
                        src={story.image_cover.url}
                        alt={getLocalized(story, 'title')}
                        className="h-full w-full object-contain rounded"
                      />
                    ) : (
                      <div className="text-white flex items-center justify-center h-full"><p className="text-gray-400">بدون فایل</p></div>
                    )}
                    {getLocalized(story, 'caption') && (
                      <div className="absolute bottom-7 left-4 right-16 z-20 pointer-events-none">
                        <p className="text-white text-sm font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                          {getLocalized(story, 'caption')}
                        </p>
                      </div>
                    )}
                    {isVideo && videoDuration[storyId] && (
                      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 text-white text-xs drop-shadow-lg bg-black/50 px-2 py-1 rounded-full pointer-events-none">
                        {formatTime(currentTime[storyId] || 0)} / {formatTime(videoDuration[storyId] || 0)}
                      </div>
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}